import { Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { catchError, debounceTime, distinctUntilChanged, of, switchMap } from 'rxjs';
import { marker } from '@colsen1991/ngx-translate-extract-marker';
import { NotificationMessageLevel, NotificationService } from '@lib/core';
import { ISupplierSearchResult, ISupplierSearchResultContact, SupplierSearchService } from '@lib/supplier';
import { DemandDataService } from '../../../../demand/demand-data.service';
import { ISupplierCandidate } from '../../../../demand/types/IDemandDraftFromPdfOutcome';

const createErrorText = marker('Die Bedarfsmeldung konnte nicht erstellt werden.');
const creationContinuesInfoText = marker(
    'Die Erstellung läuft weiter. Eine daraus entstandene Bedarfsmeldung ist als Entwurf verfügbar und kann über den Button „Neuen Bedarf melden" geöffnet werden.',
);

export class PdfSupplierSelectionDialogData {
    constructor(
        public readonly file: File,
        public readonly companyCodeId: string,
        public readonly candidates: ISupplierCandidate[],
        public readonly detectedSupplierName?: string,
        public readonly detectedContactName?: string,
        public readonly detectedContactEmail?: string,
    ) {}
}

export class PdfSupplierSelectionDialogResult {
    constructor(public readonly demandId: string) {}
}

/**
 * Supplier selection after an AMBIGUOUS PDF-analysis outcome (EPROC-11092): the uploaded PDF stays
 * visible on the left while the user picks one of the restricted candidates — or searches across
 * all suppliers visible to them (same restriction as the demand's supplier dialog) — and then
 * confirms the contact person, with the contact detected in the document preselected when it
 * matches. Confirming re-runs the analysis with the chosen supplier and closes with the created
 * demand's id.
 */
@Component({
    selector: 'pdf-supplier-selection-dialog',
    templateUrl: './pdf-supplier-selection-dialog.component.html',
    styleUrls: ['./pdf-supplier-selection-dialog.component.scss'],
    standalone: false,
})
export class PdfSupplierSelectionDialogComponent {
    public readonly data = inject<PdfSupplierSelectionDialogData>(MAT_DIALOG_DATA);
    private readonly dialogRef =
        inject<MatDialogRef<PdfSupplierSelectionDialogComponent, PdfSupplierSelectionDialogResult>>(MatDialogRef);
    private readonly supplierSearchService = inject(SupplierSearchService);
    private readonly demandDataService = inject(DemandDataService);
    private readonly notificationService = inject(NotificationService);
    private readonly destroyRef = inject(DestroyRef);

    readonly step = signal<'supplier' | 'contact'>('supplier');

    readonly searchControl = new FormControl<string>('', { nonNullable: true });
    /** `null` while the user has not searched — then only the candidates are shown. */
    readonly searchResults = signal<ISupplierSearchResult[] | null>(null);
    readonly searchRunning = signal(false);

    readonly selectedSupplier = signal<ISupplierCandidate | null>(null);

    readonly contacts = signal<ISupplierSearchResultContact[]>([]);
    readonly contactsLoading = signal(false);
    /** Empty string = "no contact person" (the supplier is assigned with its standard contact). */
    readonly contactControl = new FormControl<string>('', { nonNullable: true });
    /** The contact that was preselected because it matches the one detected in the document. */
    readonly preselectedContactId = signal<string | null>(null);

    readonly isCreating = signal(false);
    readonly createError = signal<string | undefined>(undefined);

    readonly pdfFiles = [this.data.file];

    readonly confirmDisabled = computed(() => !this.selectedSupplier() || this.isCreating());

    /** Set when the user closed the dialog while the demand creation was still running. */
    private cancelled = false;

    constructor() {
        this.searchControl.valueChanges
            .pipe(
                debounceTime(300),
                distinctUntilChanged(),
                switchMap((term: string) => {
                    const query = term.trim();
                    if (!query) {
                        this.searchRunning.set(false);
                        return of(null);
                    }
                    this.searchRunning.set(true);
                    // catchError INSIDE the switchMap: an HTTP error must not terminate the
                    // valueChanges pipeline, otherwise the search silently stops working.
                    return this.supplierSearchService
                        .search(query, true, this.data.companyCodeId)
                        .pipe(catchError(() => of([])));
                }),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe((results: ISupplierSearchResult[] | null) => {
                this.searchRunning.set(false);
                this.searchResults.set(results);
            });
    }

    selectSupplier(supplier: ISupplierCandidate): void {
        this.selectedSupplier.set(supplier);
    }

    isSelected(supplier: ISupplierCandidate): boolean {
        return this.selectedSupplier()?.id === supplier.id;
    }

    /**
     * One-line address of a candidate/search result ("street, zip city, country"). Parts the master
     * data does not carry are left out instead of showing empty separators.
     */
    addressLine(supplier: ISupplierCandidate): string {
        const cityLine = [supplier.zip, supplier.city].filter((part) => !!part?.trim()).join(' ');
        return [supplier.street, cityLine, supplier.country]
            .map((part) => part?.trim())
            .filter((part) => !!part)
            .join(', ');
    }

    proceedToContactSelection(): void {
        const supplier = this.selectedSupplier();
        if (!supplier) {
            return;
        }
        this.step.set('contact');
        this.loadContacts(supplier.id);
    }

    backToSupplierSelection(): void {
        this.step.set('supplier');
        this.createError.set(undefined);
    }

    confirm(): void {
        const supplier = this.selectedSupplier();
        if (!supplier || this.isCreating()) {
            return;
        }
        this.isCreating.set(true);
        this.createError.set(undefined);
        // Backdrop click / ESC would bypass cancel() and drop the running creation silently;
        // the explicit cancel/back buttons (and header X) keep working through cancel().
        this.dialogRef.disableClose = true;
        // Deliberately not bound to the component lifecycle: closing the dialog while the request
        // is running must not abort the creation (see cancel()).
        this.demandDataService
            .analyzePdfToDraftForSupplier(
                this.data.file,
                this.data.companyCodeId,
                supplier.id,
                this.contactControl.value || undefined,
            )
            .subscribe({
                next: (result) => {
                    if (this.cancelled) {
                        return;
                    }
                    this.dialogRef.close(new PdfSupplierSelectionDialogResult(result.demandId));
                },
                error: (errorResponse) => {
                    if (this.cancelled) {
                        return;
                    }
                    this.isCreating.set(false);
                    this.dialogRef.disableClose = false;
                    const message = typeof errorResponse?.error === 'string' ? errorResponse.error : undefined;
                    this.createError.set(message || createErrorText);
                },
            });
    }

    cancel(): void {
        if (this.isCreating()) {
            // The creation keeps running server-side; the resulting draft stays reachable as the
            // unannounced draft. Only the navigation/result handling is dropped.
            this.cancelled = true;
            this.notificationService.showNotification(creationContinuesInfoText, NotificationMessageLevel.INFO);
        }
        this.dialogRef.close();
    }

    private loadContacts(supplierCompanyId: string): void {
        this.contactsLoading.set(true);
        this.contacts.set([]);
        this.preselectedContactId.set(null);
        this.contactControl.setValue('');
        this.supplierSearchService.getContacts(supplierCompanyId).subscribe({
            next: (contacts: ISupplierSearchResultContact[]) => {
                const named = contacts.filter((contact) => !!contact.person?.id);
                this.contacts.set(named);
                this.preselectDetectedContact(named);
                this.contactsLoading.set(false);
            },
            error: () => {
                this.contacts.set([]);
                this.contactsLoading.set(false);
            },
        });
    }

    /**
     * Preselects the contact detected in the document by exact full-name match (case-insensitive;
     * the contact DTO carries no e-mail, the e-mail tiebreak already happened server-side). A
     * missing or ambiguous match preselects nothing, so the user decides consciously.
     */
    private preselectDetectedContact(contacts: ISupplierSearchResultContact[]): void {
        const detectedName = this.data.detectedContactName?.trim().toLowerCase();
        if (!detectedName) {
            return;
        }
        const matches = contacts.filter((contact) => {
            const person = contact.person;
            const fullName = `${person.firstName ?? ''} ${person.surName ?? ''}`.trim().toLowerCase();
            return fullName === detectedName || person.name?.trim().toLowerCase() === detectedName;
        });
        if (matches.length === 1) {
            this.contactControl.setValue(matches[0].person.id);
            this.preselectedContactId.set(matches[0].person.id);
        }
    }
}
